import { Globe, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/context/LanguageContext";

const FLAG_EMOJIS = {
  US: "\uD83C\uDDFA\uD83C\uDDF8",
  IN: "\uD83C\uDDEE\uD83C\uDDF3",
  ES: "\uD83C\uDDEA\uD83C\uDDF8",
  FR: "\uD83C\uDDEB\uD83C\uDDF7",
  DE: "\uD83C\uDDE9\uD83C\uDDEA",
  JP: "\uD83C\uDDEF\uD83C\uDDF5",
  CN: "\uD83C\uDDE8\uD83C\uDDF3",
  SA: "\uD83C\uDDF8\uD83C\uDDE6",
  BR: "\uD83C\uDDE7\uD83C\uDDF7",
  KR: "\uD83C\uDDF0\uD83C\uDDF7",
};

export function LanguageSelector({ variant = "default", showLabel = false }) {
  const { currentLanguage, setCurrentLanguage, supportedLanguages, t } = useLanguage();

  const selectedLang = supportedLanguages.find((l) => l.code === currentLanguage);

  const handleLanguageChange = (code) => {
    setCurrentLanguage(code);
  };

  if (variant === "minimal") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 gap-1.5"
            data-testid="language-selector-minimal"
          >
            <span className="text-base">{FLAG_EMOJIS[selectedLang?.flag]}</span>
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="text-xs text-slate-500 uppercase tracking-wider">
            {t("label.select_language")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {supportedLanguages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className="flex items-center justify-between"
              data-testid={`lang-option-${lang.code}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-base">{FLAG_EMOJIS[lang.flag]}</span>
                <span className="text-sm">{lang.name}</span>
              </div>
              {currentLanguage === lang.code && (
                <Check className="w-4 h-4 text-cyan-600" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === "compact") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2.5 gap-1.5 text-slate-600 border-slate-200"
            data-testid="language-selector-compact"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="text-xs font-medium uppercase">{currentLanguage}</span>
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-xs text-slate-500 uppercase tracking-wider">
            {t("label.language")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {supportedLanguages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className="flex items-center justify-between"
              data-testid={`lang-option-${lang.code}`}
            >
              <div className="flex items-center gap-2.5">
                <span className="text-base">{FLAG_EMOJIS[lang.flag]}</span>
                <div>
                  <span className="text-sm">{lang.name}</span>
                  <span className="text-xs text-slate-400 ml-1.5">{lang.nativeName}</span>
                </div>
              </div>
              {currentLanguage === lang.code && (
                <Check className="w-4 h-4 text-cyan-600" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default variant
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 px-3 gap-2 text-slate-700 border-slate-200 hover:bg-slate-50"
          data-testid="language-selector"
        >
          <Globe className="w-4 h-4 text-slate-500" />
          {showLabel && <span className="text-sm">{selectedLang?.name}</span>}
          {!showLabel && (
            <span className="text-sm font-medium uppercase">{currentLanguage}</span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
          <Globe className="w-3.5 h-3.5" />
          {t("label.select_language")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className="flex items-center justify-between py-2"
            data-testid={`lang-option-${lang.code}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{FLAG_EMOJIS[lang.flag]}</span>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-900">{lang.name}</span>
                <span className="text-xs text-slate-400">{lang.nativeName}</span>
              </div>
            </div>
            {currentLanguage === lang.code && (
              <Check className="w-4 h-4 text-cyan-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
